// src/contexts/NotificationContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getUserNotifications, 
  markNotificationAsRead, 
  markAllNotificationsAsRead,
  deleteNotification
} from '../services/enhancedFirestore';
import { useAuth } from './AuthContext';

// Create Context
const NotificationContext = createContext();

// Notification Provider Component
export const NotificationProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch notifications when user changes
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!currentUser) {
        setNotifications([]);
        setUnreadCount(0);
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        const userNotifications = await getUserNotifications(currentUser.uid);
        
        // Set notifications and calculate unread count
        setNotifications(userNotifications);
        setUnreadCount(userNotifications.filter(notification => !notification.read).length);
        
      } catch (error) {
        console.error('Error fetching notifications:', error);
        setError('Failed to load notifications');
      } finally {
        setLoading(false);
      }
    };
    
    fetchNotifications();
    
    // Set up a polling mechanism to check for new notifications every minute
    // In a production app, this would ideally use a real-time listener or push notifications
    const interval = setInterval(fetchNotifications, 60000);
    
    return () => clearInterval(interval);
  }, [currentUser]);
  
  // Mark a notification as read
  const markAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
      
      // Update local state
      setNotifications(prevNotifications => 
        prevNotifications.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read: true } 
            : notification
        )
      );
      
      // Recalculate unread count
      setUnreadCount(prevCount => Math.max(0, prevCount - 1));
      
      return true;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return false;
    }
  };
  
  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!currentUser) return false;
    
    try {
      await markAllNotificationsAsRead(currentUser.uid);
      
      // Update local state
      setNotifications(prevNotifications => 
        prevNotifications.map(notification => ({ ...notification, read: true }))
      );
      
      // Reset unread count
      setUnreadCount(0);
      
      return true;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return false;
    }
  };
  
  // Delete a notification
  const removeNotification = async (notificationId) => {
    try {
      await deleteNotification(notificationId);
      
      // Update local state
      const updatedNotifications = notifications.filter(
        notification => notification.id !== notificationId
      );
      
      setNotifications(updatedNotifications);
      
      // Recalculate unread count
      setUnreadCount(updatedNotifications.filter(notification => !notification.read).length);
      
      return true;
    } catch (error) {
      console.error('Error deleting notification:', error);
      return false;
    }
  };
  
  // Refresh notifications
  const refreshNotifications = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const userNotifications = await getUserNotifications(currentUser.uid);
      
      // Set notifications and calculate unread count
      setNotifications(userNotifications);
      setUnreadCount(userNotifications.filter(notification => !notification.read).length);
      
    } catch (error) {
      console.error('Error refreshing notifications:', error);
      setError('Failed to refresh notifications');
    } finally {
      setLoading(false);
    }
  };
  
  // Context value
  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    removeNotification,
    refreshNotifications
  };
  
  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook for using the notification context
export const useNotifications = () => {
  return useContext(NotificationContext);
};

export default NotificationContext;